/**
 * WebM 書き出し（Phase I 再設計、ブラウザ標準 MediaRecorder 経由）。
 *
 * ffmpeg.wasm ベースの MP4 実装（Phase I 初版）は COEP / CDN / iframe sandbox
 * など環境依存の問題で安定しなかったため、**ブラウザ標準 API のみ**で動く
 * WebM 書き出しへ全面差し替えた。ビルドに追加依存はなく、同 origin だけで
 * 完結するのでデプロイ先のセキュリティ設定にも影響しない。
 *
 * 仕様:
 * - **コンテナ / コーデック**: WebM / **VP9 優先、VP8 にフォールバック**。
 *   どちらも Chrome / Safari 17+ / Firefox / Edge で再生可能。macOS の
 *   QuickTime 標準は未対応のため Chrome / VLC で確認する。
 * - **フレームレート**: 30fps。`canvas.captureStream(FPS)` が生成する
 *   MediaStream の周期サンプリング rate として指定する。
 * - **ビットレート**: 8 Mbps（GIF 書き出しと比べて大幅に高品質）。
 * - **モード**: GIF / PNG と同じ `state.exportMode` に従い
 *   Detonate / Idle Loop / Full Story に分岐。
 *
 * Phase J（Export Timing Controls）:
 * - Detonate モード = `prePulseDuration`（0/1/2/3 s）+ `detonateDuration`
 *   （5/4/3/2/1/0.5 s）。`prePulseDuration` の間は `progress = 0` 固定で
 *   silhouette が Pulse Breath で呼吸し、以降で `progress 0→1` を
 *   `detonateDuration` 秒で進める。
 * - Full Story = 3.0 s 固定イントロ + `detonateDuration`（prePulse は無効）。
 * - Idle Loop = 変更なし（`pulsePeriod` 秒ぶんの `progress=0` フレーム）。
 *
 * 録画フロー:
 * 1. `MediaRecorder.isTypeSupported` で VP9 → VP8 → 無指定 の順に使える
 *    mimeType を探す。
 * 2. `canvas.captureStream(FPS)` で canvas の MediaStream を取得。
 * 3. `MediaRecorder` 生成 → `start()` で録画開始。
 * 4. `playAnimation` で setTimeout ベースの pacing により各フレームを
 *    `render(ctx, frameState)` で描画。MediaRecorder は stream から自動で
 *    pull するので、本モジュールは **明示的な frame push をしない**
 *    （captureStream が canvas 変化を自動で拾う）。
 * 5. 最後まで再生し終わったら `recorder.stop()`。`onstop` で全 chunk を
 *    Blob にまとめる。
 * 6. Blob を URL.createObjectURL でダウンロード発火。
 *
 * 設計参照:
 * - docs/happa-designtool.md Phase I（WebM 書き出し）
 * - https://developer.mozilla.org/docs/Web/API/MediaRecorder
 */

import { render } from '../render';
import type { AppState } from '../state';

/** 動画フレームレート（全モード共通）。 */
const FPS = 30;

/** Full Story モードの idle intro 部の秒数（固定、Phase J でも未パラメータ化）。 */
const FULL_STORY_IDLE_INTRO_S = 3.0;

/** VP9 / VP8 に指定するビットレート（8 Mbps、高品質 + 軽量配信の中間）。 */
const VIDEO_BITRATE = 8_000_000;

/**
 * 書き出しの各局面。UI overlay の文言切り替えに使う。
 *
 * - `'recording'`: MediaRecorder が録画中。`ratio` は再生進捗（0..1）。
 * - `'finalizing'`: `recorder.stop()` → `onstop` までの Blob 組立て待ち。
 * - `'done'`: ダウンロード発火完了。
 * - `'error'`: 例外発生。呼び出し側でハンドリング。
 */
export type WebMExportPhase = 'recording' | 'finalizing' | 'done' | 'error';

/** `exportWebM` の進捗通知 callback（0..1）。 */
export type WebMProgressCallback = (phase: WebMExportPhase, ratio: number) => void;

/**
 * 現在の `state` に基づき WebM を書き出してダウンロードする。
 *
 * 実行は非同期。録画開始 → 各フレーム描画（FPS pacing）→ 録画停止 → Blob
 * 組立て → ダウンロードまで 1 本で走る。各フェーズで `onProgress` が呼ばれる
 * （`'recording'` のみ 0..1 の進捗、他は完了点を示す）。
 *
 * エラー時: WebM 非対応ブラウザや MediaRecorder 例外を `'error'` で通知して
 * から throw する。呼び出し側は try/catch して UI 復帰する責務を持つ。
 *
 * canvas state の復元: 書き出し中は一時 `AppState` で `progress` を上書き
 * しつつ `render(ctx, ...)` を直接呼ぶ（store は触らない）。終了後は UI 側の
 * 通常描画サイクルが戻り、ユーザが見ていた最後の状態に戻る（gif.ts と同じ方針）。
 *
 * @param canvas メインの描画 canvas（`captureStream` の元）。
 * @param state アプリの現在 state（`exportMode` で分岐）。
 * @param onProgress 進捗通知（任意）。
 */
export async function exportWebM(
  canvas: HTMLCanvasElement,
  state: AppState,
  onProgress?: WebMProgressCallback,
): Promise<void> {
  const mimeType = pickMimeType();
  if (mimeType === null) {
    onProgress?.('error', 1);
    throw new Error('WebM (VP9/VP8) is not supported in this browser');
  }

  const ctx = canvas.getContext('2d');
  if (ctx === null) {
    onProgress?.('error', 1);
    throw new Error('canvas 2D context is not available');
  }

  const stream = canvas.captureStream(FPS);
  const recorder = new MediaRecorder(stream, {
    mimeType,
    videoBitsPerSecond: VIDEO_BITRATE,
  });

  const chunks: Blob[] = [];
  recorder.addEventListener('dataavailable', (e) => {
    // size 0 のチャンクは空 pull（ストリーム停止時等）なので無視する。
    if (e.data.size > 0) chunks.push(e.data);
  });

  // recorder.stop() を待ち受ける Promise。onstop で Blob 集約、onerror で reject。
  const recordingDone = new Promise<Blob>((resolve, reject) => {
    recorder.addEventListener('stop', () => {
      resolve(new Blob(chunks, { type: mimeType }));
    });
    recorder.addEventListener('error', (e) => {
      reject(new Error(`MediaRecorder error: ${String(e)}`));
    });
  });

  onProgress?.('recording', 0);
  recorder.start();

  try {
    await playAnimation(ctx, state, onProgress);
  } finally {
    // playAnimation 内で例外が起きても stop() は必ず呼ぶ（MediaRecorder を
    // 放置すると後続の exportWebM 呼び出しで stream 競合が起きる）。
    if (recorder.state !== 'inactive') recorder.stop();
  }

  onProgress?.('finalizing', 0.95);
  const blob = await recordingDone;

  downloadBlob(blob, `happa-${Date.now()}.webm`);
  onProgress?.('done', 1);
}

// ── 内部 helpers ──────────────────────────────────────────────────────

/**
 * ブラウザでサポートされる WebM mimeType を優先順で探す。
 *
 * 優先順位（画質・互換性の両立）:
 * 1. `video/webm;codecs=vp9` — 現代ブラウザの標準、高圧縮
 * 2. `video/webm;codecs=vp8` — 古い Firefox / Safari フォールバック
 * 3. `video/webm` — コーデック未指定（ブラウザ任せ）
 *
 * どれも使えない環境では null を返す。Safari 17 未満は WebM 非対応のため
 * 該当する（この場合は exportWebM の冒頭で throw される）。
 */
function pickMimeType(): string | null {
  const candidates = ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'];
  for (const candidate of candidates) {
    if (MediaRecorder.isTypeSupported(candidate)) return candidate;
  }
  return null;
}

/**
 * モードに応じて canvas にフレームを描画し続け、MediaRecorder が自動で拾う
 * stream を駆動する。
 *
 * pacing は `performance.now()` 基準の単純 catch-up。フレーム N の描画後に
 * 理論上の「次フレーム時刻」までだけ待機する。前フレームが遅れても次は
 * 追いつかせに行く（録画秒数が伸びすぎないよう）。短クリップ（2〜5 秒）
 * では十分。
 *
 * **重要**: `render(ctx, frameState)` は canvas の **内容**を書き換えるだけで、
 * `captureStream` は内部で canvas の変更を自動検知してストリームに反映する。
 * このため本関数は「フレームを積む」操作を明示的にしない（MediaRecorder の
 * pull モデルに任せる）。
 */
async function playAnimation(
  ctx: CanvasRenderingContext2D,
  state: AppState,
  onProgress: WebMProgressCallback | undefined,
): Promise<void> {
  const plan = computeAnimationPlan(state);
  const { totalDuration } = plan;
  const frameCount = Math.max(1, Math.round(totalDuration * FPS));
  const frameDurationMs = 1000 / FPS;
  const startTime = performance.now();

  for (let i = 0; i < frameCount; i++) {
    // 正規化時刻 t（秒）と対応する progress を合わせて frameState を作る。
    // totalDuration ちょうどで進行を 1 に収めるため `(i + 1) / frameCount`
    // ではなく「最終フレームを含まない」ラチェット式（0..1-1/N）を採用。
    const normalizedT = (i / Math.max(1, frameCount - 1)) * totalDuration;
    const progress = computeProgress(normalizedT, plan);

    const frameState: AppState = { ...state, progress };
    render(ctx, frameState);

    onProgress?.('recording', (i + 1) / frameCount);

    // 次フレーム時刻まで待機。catch-up: 遅延していたら待たず即次へ。
    const targetTime = (i + 1) * frameDurationMs;
    const elapsed = performance.now() - startTime;
    const wait = targetTime - elapsed;
    if (wait > 0) await sleep(wait);
  }
}

/**
 * 書き出し計画（全モード共通シェイプ、Phase J）。
 *
 * `totalDuration` に「前置き秒数 + 爆破秒数」を、`idleDuration` に「progress=0
 * で固定しておく先頭の秒数」を格納する。`detonateDuration` は `idleDuration`
 * の後に続く 0→1 進行の秒数。Idle Loop では `detonateDuration = 0` とし、
 * `computeProgress` 側が常に 0 を返す。
 */
interface AnimationPlan {
  totalDuration: number;
  idleDuration: number;
  detonateDuration: number;
  mode: AppState['exportMode'];
}

/**
 * `exportMode` とタイミングフィールドから動画プランを組み立てる（Phase J）。
 *
 * - `'detonate'`: `prePulseDuration` 秒の呼吸 → `detonateDuration` 秒で 0→1。
 *   両方ユーザ調整可（Pre-Pulse 4 段階 / Detonate Speed 6 段階）。
 * - `'idle-loop'`: `pulsePeriod` 秒ぶん progress=0（breath のみ動く、従来通り）。
 * - `'full-story'`: 3.0 s 固定 idle intro → `detonateDuration` 秒で 0→1。
 *   Full Story では Pre-Pulse は無効化（intro 3 秒のほうが優先）。
 */
function computeAnimationPlan(state: AppState): AnimationPlan {
  const detonateDuration = Math.max(0.05, state.detonateDuration);
  switch (state.exportMode) {
    case 'detonate': {
      const idleDuration = Math.max(0, state.prePulseDuration);
      return {
        totalDuration: idleDuration + detonateDuration,
        idleDuration,
        detonateDuration,
        mode: 'detonate',
      };
    }
    case 'idle-loop':
      return {
        totalDuration: state.breath.pulsePeriod,
        idleDuration: state.breath.pulsePeriod,
        detonateDuration: 0,
        mode: 'idle-loop',
      };
    case 'full-story':
      return {
        totalDuration: FULL_STORY_IDLE_INTRO_S + detonateDuration,
        idleDuration: FULL_STORY_IDLE_INTRO_S,
        detonateDuration,
        mode: 'full-story',
      };
    default: {
      const _exhaustive: never = state.exportMode;
      console.error('[export/webm] unknown exportMode:', _exhaustive);
      return {
        totalDuration: detonateDuration,
        idleDuration: 0,
        detonateDuration,
        mode: 'detonate',
      };
    }
  }
}

/**
 * 指定時刻 `t` （秒）で描画すべき `progress` 値を返す（Phase J）。
 *
 * `idleDuration` の内は 0（呼吸のみ）。それ以降は `(t - idleDuration) /
 * detonateDuration` を 1 以下に clamp した値。`detonateDuration === 0`
 * （Idle Loop）では常に 0 を返す（ゼロ除算防止）。
 */
function computeProgress(t: number, plan: AnimationPlan): number {
  if (plan.detonateDuration <= 0) return 0;
  if (t < plan.idleDuration) return 0;
  return Math.min(1, (t - plan.idleDuration) / plan.detonateDuration);
}

/** `setTimeout` ベースの sleep util。 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Blob をダウンロード（`<a download>` 経由、gif.ts と同じ実装）。 */
function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
